/**
 * OPO — Cornelius gated tool approvals (Tier-3).
 * Proxies: /api/opo/cornelius-memories/tool-approvals/*
 */
import React, { useState, useEffect, useCallback } from 'react';

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)',
  padding: '12px 14px',
  marginBottom: 10,
  color: 'var(--text-primary)',
};

export default function AdminCorneliusApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [tokens, setTokens] = useState({});
  const [decidedBy] = useState('opo-admin');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/opo/cornelius-memories/tool-approvals/pending?limit=80');
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      setRows(d.pending || []);
      setError('');
    } catch (e) {
      setError(e.message);
      setRows([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function approve(id) {
    setBusyId(id);
    try {
      const r = await fetch(`/api/opo/cornelius-memories/tool-approvals/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decided_by: decidedBy }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      setTokens((t) => ({ ...t, [id]: d.approval_token || '' }));
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusyId(null);
  }

  async function deny(id) {
    const reason = window.prompt('Denial reason (optional):') || '';
    setBusyId(id);
    try {
      const r = await fetch(`/api/opo/cornelius-memories/tool-approvals/${id}/deny`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decided_by: decidedBy, reason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusyId(null);
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 14, fontSize: 13, color: 'var(--text-secondary)' }}>
        Pending / approved-but-not-executed tool calls awaiting a one-shot token. Approve rows here, then paste the
        UUID into the research task resume field on the <strong>Tasks</strong> sub-tab (or POST resume-approval).
      </div>
      <button
        type="button"
        onClick={load}
        style={{
          padding: '7px 16px',
          borderRadius: 'var(--border-radius)',
          border: 'none',
          background: 'var(--primary-color)',
          color: 'var(--text-on-primary)',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: 14,
        }}
      >
        Refresh
      </button>

      {error && <div style={{ color: 'var(--danger-color)', marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No pending approvals.</div>
      )}

      {rows.map((p) => (
        <div key={p.id} style={card}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <strong>#{p.id}</strong>
            <code style={{ fontSize: 12 }}>{p.tool_name}</code>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>user {p.user_id}</span>
            <span style={{
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 6,
              background: 'var(--surface-color)',
              border: '1px solid var(--border-color)',
            }}>{p.status}</span>
          </div>
          {p.task_ref && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              task_ref <code>{p.task_ref}</code>
            </div>
          )}
          <pre style={{
            marginTop: 8,
            fontSize: 11,
            overflow: 'auto',
            maxHeight: 140,
            background: 'var(--surface-color)',
            padding: 8,
            borderRadius: 6,
            border: '1px solid var(--border-color)',
          }}>{JSON.stringify(p.args_json, null, 2)}</pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={busyId === p.id || p.status !== 'pending'}
              onClick={() => approve(p.id)}
              style={{ padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={busyId === p.id || p.status !== 'pending'}
              onClick={() => deny(p.id)}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                border: '1px solid var(--danger-color)',
                background: 'transparent',
                color: 'var(--danger-color)',
              }}
            >
              Deny
            </button>
          </div>
          {tokens[p.id] && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--success-color)', marginBottom: 4 }}>
                Paste this approval token into resume field for the paused task:
              </div>
              <code style={{ fontSize: 12, wordBreak: 'break-all', color: 'var(--primary-color)' }}>
                {tokens[p.id]}
              </code>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
